import cx from 'classnames';
import PropTypes from 'prop-types';
import { PureComponent, Fragment } from 'react';

import withViewportBreakpoint from 'core/hocs/withViewportBreakpoint';
import { viewportBreakpoints } from 'core/components/breakpoint/viewport-values';

import viewportBreakpointPropTypes from 'core/utils/prop-types/viewport-breakpoint';
import breakpointPropTypes from 'core/utils/prop-types/breakpoint';
import { imageVersionAttributes } from 'core/utils/prop-types/model';
import { isVideoMedia } from 'core/utils/content';

import getImageUrl, { getMinValue, isSaveData, getSrc } from 'core/utils/smart-image';

import VideoPlayer from './VideoPlayer';
import styles from './index.styl';


const DEFAULT_RATIO = 16 / 10;
const PREVIEW_SIZE = 60;

class SmartImage extends PureComponent {
  static propTypes = {
    /**
     * Соотношение сторон изображения.
     * Перекрывает изначальное соотношение сторон.
     */
    aspectRatio: PropTypes.number,

    /**
     * Строго заданная ширина элемента, которая в приоритете над `aspectRatio`.
     */
    width: PropTypes.number,

    /**
     * Строго заданная ширина элемента, которая в приоритете над `aspectRatio`.
     */
    height: PropTypes.number,

    /**
     * Максимальная ширина, которая может задана пользователем.
     */
    maxWidth: PropTypes.number,

    /**
     * Максимальная высота, которая может задана пользователем.
     */
    maxHeight: PropTypes.number,

    /**
     * Основной атрибут, из которого компонент берет URL для изображения,
     * которое будет выведено на экран. Атрибут берется из API.
     */
    src: imageVersionAttributes,

    /**
     * URL внешнего изображения.
     */
    url: PropTypes.string,

    /**
     * Url thumbnail-изображения, из которого компонент будет рисовать blur-preview.
     * Атрибут берется из API.
     */
    previewSrc: imageVersionAttributes,

    /**
     * Отключение ленивой загрузки изображения.
     */
    instant: PropTypes.bool,

    /**
     * Cвойство, которое указывает на версию изображения
     */
    version: PropTypes.string,

    /**
     * Атрибут берется из API, тут храниться все версии изображения.
     */
    versions: PropTypes.object,

    /**
     * Указываем каким способом отрисовывать изображение и маску в контейнере
     * изображения.
     */
    placement: PropTypes.oneOf([
      'cover',
      'contain',
    ]),

    /**
     * Параметр отвечает за background-position
     */
    position: PropTypes.string,

    /** @ignore */
    viewportBreakpoint: viewportBreakpointPropTypes(),

    /** @ignore */
    breakpoint: breakpointPropTypes(),

    /** ref callback для картинки, чтобы иметь доступ к самому элементу */
    imageRef: PropTypes.func,

    /**
     * Атрибут alt для изображения
     */
    alt: PropTypes.string,

    /**
     * Атрибут title для изображения
     */
    title: PropTypes.string,

    /**
     * Приоритет подгрузки изображения
     */
    fetchPriority: PropTypes.string,

    /**
     * Размечать ли картинку с помощью ImageObject
     */
    useSchema: PropTypes.bool,

    /**
     * URL до webdav с изображениями.
     * Это свойство перебивает аналогичное свойство из контекста приложения
     */
    webdavHost: PropTypes.string,

    /**
     * URL до инстанса thumbor.
     * Это свойство перебивает аналогичное свойство из контекста приложения
     */
    thumborHost: PropTypes.string,

    className: PropTypes.string,

    /**
     * Параметр smart включает умную нарезку.
     * Для работы необходимо передать фиксированную ширину и высоту
     */
    smart: PropTypes.bool,

    /**
     * Коллбек, вызываемый при успешной загрузке изображения
     */
    onImageLoaded: PropTypes.func,

    /**
     * Коллбек, вызываемый при ошибке загрузки изображения
     */
    onImageLoadingError: PropTypes.func,

    /**
     * Это свойство отвечает за то, что при его включении ширина изображения берется из src автоматически
     */
    widthFromSrc: PropTypes.bool,

    /**
     * Обработчик клика по картинке
     */
    onClick: PropTypes.func,
  };

  static contextTypes = {
    siteName: PropTypes.string,
    webdavHost: PropTypes.string,
    thumborHost: PropTypes.string,
    features: PropTypes.shape({
      disableBlurPreview: PropTypes.bool,
      enableRetina: PropTypes.bool,
    }),
  };

  static defaultProps = {
    instant: false,
    placement: 'cover',
    position: 'center',
    version: 'original',
    alt: '',
  };

  state = {
    imageLoaded: false,
  };

  componentDidMount() {
    const {
      imageLoaded,
      imageLoadFail,
    } = this.state;

    if (this.imageElement?.complete && !(imageLoaded || imageLoadFail)) {
      this.imageLoaded();
    }
  }

  get isVideo() {
    const src = getSrc(this.props);
    return isVideoMedia(src, this.props.url);
  }

  imageLoaded = () => {
    this.setState({ imageLoaded: true });
    this.props.onImageLoaded && this.props.onImageLoaded();
  };

  imageLoadFail = () => {
    this.setState({ imageLoadFail: true });
    this.props.onImageLoadingError && this.props.onImageLoadingError();
  };

  setImageRef = element => {
    const { imageRef } = this.props;
    imageRef && typeof imageRef === 'function' && imageRef(element);

    this.imageElement = element;
  };

  render() {
    const {
      url,
      height,
      instant,
      position,
      maxWidth,
      maxHeight,
      placement,
      aspectRatio,
      viewportBreakpoint,
      alt,
      fetchPriority,
      title,
      useSchema,
      className,
      smart,
      versions,
      widthFromSrc,
      onClick,
    } = this.props;

    const src = getSrc(this.props);

    const width = widthFromSrc && src ? src.width : this.props.width;

    const previewSrc = this.props.previewSrc || versions?.thumbnail;

    const { imageLoaded, imageLoadFail } = this.state;

    if (!url && !src) {
      return null;
    }

    const {
      features: {
        disableBlurPreview,
        enableRetina,
      },
    } = this.context;

    const webdavHost = this.props.webdavHost || this.context.webdavHost;
    const thumborHost = this.props.thumborHost || this.context.thumborHost;

    /**
     * Определяем размер картинки для нарезки
     */
    let imageWidth = getMinValue(width, maxWidth);

    /**
     * Ограничиваем максимальную ширину картинки максимальной шириной
     * брекпоинта вьюпорта.
     */
    const viewportMaxWidth = parseInt(viewportBreakpoints[viewportBreakpoint], 10);

    if (viewportMaxWidth && (!imageWidth || imageWidth > viewportMaxWidth)) {
      imageWidth = viewportMaxWidth;
    }

    const commonImageUrlProps = {
      src,
      url,
      width,
      height,
      maxWidth: imageWidth,
      maxHeight,
      aspectRatio,
      webdavHost,
      smart,
      ...!this.isVideo && { thumborHost },
    };

    const imageUrl = getImageUrl(commonImageUrlProps);
    const retinaImageUrl = enableRetina && !isSaveData() && getImageUrl({ ...commonImageUrlProps, dpr: 2 });

    const ratioSrc = src?.width / src?.height || DEFAULT_RATIO;

    const needPreview = !!previewSrc || !src?.url && !url && !instant;

    let previewUrl;

    if (needPreview) {
      const hasPreviewUrl = !!previewSrc?.rel_url;
      const ratioPreviewSrc = previewSrc?.width / previewSrc?.height || DEFAULT_RATIO;
      /**
       * Если соотношения сторон картинки и превьюшки разные, то превьюшку
       * нужно будет нарезать. Сравниваем до 1 знака после запятой, причем
       * без округления, поэтому не используем toFixed.
       */
      const cropPreview = Math.trunc(ratioSrc * 10) / 10 !== Math.trunc(ratioPreviewSrc * 10) / 10;
      /**
       * Обрабатываем превью через thumbor когда:
       * 1. нужно добавить blur;
       * 2. разное соотношение сторон;
       * 3. нет thumbnail в API (переобложки в качестве превью исползуют основное изображение).
       *
       * TODO: Проверку на `disableBlurPreview` можно будет убрать,
       * когда все `thumbnails` из API будут приходить заблеренными.
       */
      if (disableBlurPreview && !cropPreview && hasPreviewUrl) {
        previewUrl = getImageUrl({
          src: previewSrc,
          webdavHost,
        });
      } else {
        previewUrl = getImageUrl({
          /**
           * Передаем размеры оригинального изображения для того, чтобы
           * нарезать превьюшку с этим соотношением сторон.
           */
          src: {
            ...src,
            ...hasPreviewUrl && { rel_url: previewSrc.rel_url },
          },
          width: PREVIEW_SIZE,
          height: PREVIEW_SIZE,
          aspectRatio: aspectRatio || width / height || ratioSrc,
          webdavHost,
          ...!this.isVideo && { thumborHost },
          fastBlur: !disableBlurPreview || !hasPreviewUrl,
          cropWithRatio: cropPreview,
          withoutQualityOptimize: true,
        });
      }
    }

    const canImgBeShown = instant || imageLoaded || imageLoadFail;
    const schemaProps = {};

    if (useSchema) {
      schemaProps.itemType = 'http://schema.org/ImageObject';
      schemaProps.itemScope = true;
    }

    const mediaProps = {
      className: styles.src,
      src: imageUrl,
      style: {
        ...position !== 'center' && { objectPosition: position },
      },
      alt,
    };

    return (
      <div
        className={cx(
          styles.image,
          canImgBeShown && styles.imgCanBeShowed,
          styles[placement],
          className,
        )}
        style={{
          ...width && { width },
          ...height && { height },
          ...maxWidth && { maxWidth },
          ...maxHeight && { maxHeight },
          aspectRatio: `${aspectRatio || width / height || ratioSrc}`,
        }}
        {...schemaProps}
      >
        {this.isVideo ? (
          <VideoPlayer
            {...mediaProps}
            onLoadCallback={this.imageLoaded}
            onError={this.imageLoadFail}
            innerRef={this.setImageRef}
          />
        ) : (
          <img
            {...mediaProps}
            ref={this.setImageRef}
            title={title}
            onClick={onClick}
            {...!this.imageElement?.complete && {
              onLoad: this.imageLoaded,
              onError: this.imageLoadFail,
            }}
            {...retinaImageUrl && {
              srcSet: `${retinaImageUrl} 2x`,
            }}
            {...fetchPriority && { fetchpriority: fetchPriority }}
            {...!instant && { loading: 'lazy' }}
          />
        )}
        {needPreview && (
          <div
            className={styles.blurred_preview}
            style={{
              backgroundPosition: position,
              backgroundImage: `url('${previewUrl}')`,
            }}
          />
        )}
        {useSchema && (
          <Fragment>
            <link itemProp='url' href={imageUrl} />
            <link itemProp='contentUrl' href={imageUrl} />
          </Fragment>
        )}
      </div>
    );
  }
}

const SmartImageWithHOCs = withViewportBreakpoint(SmartImage);

SmartImageWithHOCs.displayName = 'SmartImage';

export { SmartImage as StorybookComponent };
export default SmartImageWithHOCs;
